/**
 * `LocalStore` — the one working `Store` adapter (SPEC §10).
 *
 * IndexedDB, falling back to localStorage. The fallback is not decoration: iOS Safari can refuse
 * to open a database in a private tab or under storage pressure, and this app's core promise is
 * that a completed review is never lost. Losing one to a closed tab is a defect (SPEC §12 DoD-5),
 * so a failed `indexedDB.open` degrades instead of throwing.
 *
 * Writes are `put` on a keyed object store, which makes them idempotent for free: replaying a
 * banked attempt after a mid-write reload lands once, because the key is the attempt id.
 */

import type { Store, StoreEntry } from './store';

const DB_NAME = 'spanish-game';
const DB_VERSION = 1;
const OBJECT_STORE = 'kv';

/** Namespace for the localStorage fallback, so we never collide with anything else on the origin. */
const LS_PREFIX = 'sg:';

/** Sorts after every character we ever put in a key, so a prefix scan has a clean upper bound. */
const KEY_RANGE_END = '￿';

export interface LocalStoreOptions {
  dbName?: string;
  /** Pass `null` to force the localStorage fallback. Tests use this to cover both paths. */
  indexedDB?: IDBFactory | null;
  /** Pass `null` to force IndexedDB with no fallback available. */
  storage?: Storage | null;
}

// ---------------------------------------------------------------------------------------------
// IndexedDB backend
// ---------------------------------------------------------------------------------------------

function promisify<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'));
  });
}

function openDb(factory: IDBFactory, dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let request: IDBOpenDBRequest;
    try {
      request = factory.open(dbName, DB_VERSION);
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(OBJECT_STORE)) db.createObjectStore(OBJECT_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'));
    request.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
}

class IdbBackend implements Store {
  constructor(private readonly db: IDBDatabase) {}

  private tx(mode: IDBTransactionMode): IDBObjectStore {
    return this.db.transaction(OBJECT_STORE, mode).objectStore(OBJECT_STORE);
  }

  async get<T>(key: string): Promise<T | undefined> {
    return (await promisify(this.tx('readonly').get(key))) as T | undefined;
  }

  async set<T>(key: string, value: T): Promise<void> {
    // `put`, not `add`: writing the same attempt id twice must overwrite, never throw and never
    // append a second row.
    await promisify(this.tx('readwrite').put(structuredClone(value), key));
  }

  async list<T>(prefix = ''): Promise<StoreEntry<T>[]> {
    const store = this.tx('readonly');
    const range =
      prefix === '' ? null : IDBKeyRange.bound(prefix, prefix + KEY_RANGE_END, false, false);
    const keys = (await promisify(
      range ? store.getAllKeys(range) : store.getAllKeys(),
    )) as IDBValidKey[];
    const values = (await promisify(range ? store.getAll(range) : store.getAll())) as T[];
    // IndexedDB returns both arrays in key order, so zipping preserves the ordering guarantee.
    return keys.map((key, i) => ({ key: String(key), value: values[i] as T }));
  }

  async delete(key: string): Promise<void> {
    await promisify(this.tx('readwrite').delete(key));
  }

  async clear(): Promise<void> {
    await promisify(this.tx('readwrite').clear());
  }
}

// ---------------------------------------------------------------------------------------------
// localStorage backend
// ---------------------------------------------------------------------------------------------

class LocalStorageBackend implements Store {
  constructor(private readonly storage: Storage) {}

  async get<T>(key: string): Promise<T | undefined> {
    const raw = this.storage.getItem(LS_PREFIX + key);
    if (raw === null) return undefined;
    return JSON.parse(raw) as T;
  }

  async set<T>(key: string, value: T): Promise<void> {
    this.storage.setItem(LS_PREFIX + key, JSON.stringify(value));
  }

  async list<T>(prefix = ''): Promise<StoreEntry<T>[]> {
    const wanted = LS_PREFIX + prefix;
    const out: StoreEntry<T>[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const full = this.storage.key(i);
      if (full === null || !full.startsWith(wanted)) continue;
      const raw = this.storage.getItem(full);
      if (raw === null) continue;
      out.push({ key: full.slice(LS_PREFIX.length), value: JSON.parse(raw) as T });
    }
    // localStorage enumeration order is implementation-defined; sort so `list` means the same
    // thing on both backends and an export is reproducible.
    out.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    return out;
  }

  async delete(key: string): Promise<void> {
    this.storage.removeItem(LS_PREFIX + key);
  }

  async clear(): Promise<void> {
    const doomed: string[] = [];
    for (let i = 0; i < this.storage.length; i++) {
      const full = this.storage.key(i);
      // Only our namespace. `storage.clear()` would take out anything else on the origin.
      if (full !== null && full.startsWith(LS_PREFIX)) doomed.push(full);
    }
    for (const key of doomed) this.storage.removeItem(key);
  }
}

// ---------------------------------------------------------------------------------------------
// The adapter
// ---------------------------------------------------------------------------------------------

/**
 * Resolves its backend lazily on the first operation, because whether IndexedDB actually works is
 * only knowable from an async open that can fail. Every public method awaits the same promise, so
 * the probe happens exactly once no matter how many writes race at boot.
 */
export class LocalStore implements Store {
  private backend: Promise<Store> | null = null;
  private resolvedKind: 'indexeddb' | 'localstorage' | 'memory' | null = null;

  constructor(private readonly options: LocalStoreOptions = {}) {}

  /** Which backend won. Reported in DECISIONS-relevant diagnostics and asserted by tests. */
  kind(): 'indexeddb' | 'localstorage' | 'memory' | null {
    return this.resolvedKind;
  }

  private resolve(): Promise<Store> {
    if (this.backend) return this.backend;
    this.backend = (async (): Promise<Store> => {
      const factory =
        this.options.indexedDB === undefined
          ? typeof indexedDB !== 'undefined'
            ? indexedDB
            : null
          : this.options.indexedDB;
      if (factory) {
        try {
          const db = await openDb(factory, this.options.dbName ?? DB_NAME);
          this.resolvedKind = 'indexeddb';
          return new IdbBackend(db);
        } catch {
          // Fall through. Private-mode Safari and storage pressure both land here.
        }
      }
      const storage =
        this.options.storage === undefined
          ? typeof localStorage !== 'undefined'
            ? localStorage
            : null
          : this.options.storage;
      if (storage) {
        try {
          // Probe, rather than trust: Safari exposes localStorage in private mode and then throws
          // on the first write.
          const probe = `${LS_PREFIX}__probe`;
          storage.setItem(probe, '1');
          storage.removeItem(probe);
          this.resolvedKind = 'localstorage';
          return new LocalStorageBackend(storage);
        } catch {
          // Fall through to the last resort.
        }
      }
      // Nothing persistent is available. Play continues in memory rather than the app refusing to
      // start: a session that cannot be saved is still worth more than no session, and the store
      // reports `memory` so the UI can say so plainly rather than pretending the data is safe.
      this.resolvedKind = 'memory';
      const { MemoryStore } = await import('./store');
      return new MemoryStore();
    })();
    return this.backend;
  }

  async get<T>(key: string): Promise<T | undefined> {
    return (await this.resolve()).get<T>(key);
  }

  async set<T>(key: string, value: T): Promise<void> {
    return (await this.resolve()).set(key, value);
  }

  async list<T>(prefix = ''): Promise<StoreEntry<T>[]> {
    return (await this.resolve()).list<T>(prefix);
  }

  async delete(key: string): Promise<void> {
    return (await this.resolve()).delete(key);
  }

  async clear(): Promise<void> {
    return (await this.resolve()).clear();
  }
}
